import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { base64ToBlob } from "../utils/b64toBlob";
import CameraIcon from "../assets/images/white-camera-icon.svg";
import NextIcon from "../assets/images/next-icon.svg";
import CaptureIcon from "../assets/images/white-capture-icon.svg";

const getURL = (path = "") =>
  new URL(
    `https://base44.app/api/apps/68e88f8d46dc20d3543f7426/integration-endpoints/Core${path}`
  );

const BMIPhotoCapture = () => {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Capture image and simulate analysis
  const handleCapture = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setIsCameraOpen(false);
    setIsAnalyzing(true);

    try {
      const formData = new FormData();
      formData.append(
        "file",
        base64ToBlob(imageSrc, "image/jpeg"),
        `health-scan-${Date.now()}.jpg`
      );
      const uploadResp = await fetch(getURL("/UploadFile"), {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      if (!uploadResp.ok) throw new Error("Upload Error");
      const fileData = await uploadResp.json();
      if (!fileData.file_url)
        throw new Error("Upload succeeded but file_url missing");

      const resp = await fetch(getURL("/InvokeLLM"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: `Analyze this facial photo and provide estimated health metrics.
Based on visible facial features, estimate:
- Age (in years, be realistic)
- Gender (Male, Female, or Other)
- Approximate height (in cm, based on facial proportions)
- Approximate weight (in kg, based on facial structure)

Provide your best estimates based on what you can observe.
Be reasonable and realistic in your estimates.`,
          file_urls: fileData.file_url,
          response_json_schema: {
            type: "object",
            properties: {
              age: { type: "number", range: [18, 110] },
              gender: { type: "string", enum: ["male", "female", "others"] },
              height: { type: "number", range: [30, 250] },
              weight: { type: "number", range: [20, 220] },
            },
          },
        }),
      });
      if (!resp.ok) throw new Error("Failed to analyze photo");
      const data = await resp.json();
      navigate("/bmi-result-edit", { state: data });
    } catch (err) {
      console.error(err);
      let message = "Failed to upload photo.";
      if (err instanceof Error) message = err.message;
      else if (typeof err === "string") message = err;
      alert(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return isAnalyzing ? (
    <div className="flex flex-col items-center justify-center min-h-[82vh] bg-primary text-white">
      <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mb-6"></div>
      <h2 className="text-2xl font-semibold mb-2">Analyzing Your Photo</h2>
      <p className="text-white/80 text-center">
        Using AI to estimate your health metrics...
      </p>
    </div>
  ) : (
    <div className="flex flex-col items-center justify-between bg-primary text-white px-6 min-h-[82vh] relative">
      <h1 className="text-2xl lg:text-5xl md:text-5xl font-bold mb-16 text-center mt-10">
        Know Your BMI
      </h1>

      <div className="flex flex-col items-center justify-center mb-16 relative z-10">
        <div className="lg:w-96 lg:h-96 md:w-96 md:h-96 mx-auto mt-7 blur-[120px] absolute bg-[#8AB2DF] opacity-60"></div>
        <div className="rounded-full p-6 relative z-20">
          <img src={CameraIcon} className="w-20 lg:w-28 md:w-28" alt="camera" />
        </div>
        <h2 className="text-xl lg:text-4xl md:text-4xl font-light mt-6 relative z-20">
          Take Your Photo
        </h2>
        <p className="text-white mt-8 text-sm lg:text-base md:text-base text-center relative z-20">
          We'll analyze your facial features to <br /> estimate health metrics
        </p>

        {capturedImage && (
          <img
            src={capturedImage}
            alt="Captured"
            className="mt-4 w-48 h-48 rounded-full object-cover border-2 border-white"
          />
        )}
      </div>

      <button
        type="button"
        onClick={() => setIsCameraOpen(true)}
        className="flex items-center text-center justify-center gap-5 px-8 py-4 w-5/12 relative rounded-full border-2 border-white/70 hover:border-blue-400 transition lg:text-xl md:text-xl text-lg font-normal"
      >
        Use Camera
        <span className="w-10 h-10 bg-white rounded-full flex items-center justify-center absolute right-3">
          <img src={NextIcon} alt="next" />
        </span>
      </button>

      {isCameraOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded-[20px] relative flex flex-col items-center">
            <h4 className="text-xl lg:text-2xl md:text-2xl font-semibold text-primary text-center mb-5">
              Position Your Face in the Frame
            </h4>
            <button
              type="button"
              onClick={() => setIsCameraOpen(false)}
              className="absolute top-4 right-4 text-black font-bold"
            >
              ✕
            </button>

            <Webcam
              audio={false}
              ref={webcamRef}
              mirrored
              screenshotFormat="image/jpeg"
              className="rounded-[20px] w-[90%] h-[80%]"
            />

            <button
              type="button"
              onClick={handleCapture}
              className="mt-8 mb-4 px-6 py-3 bg-black text-white text-lg lg:text-xl md:text-xl rounded-full flex items-center justify-center"
            >
              <img src={CaptureIcon} className="w-6 mr-3" alt="" />
              Capture Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BMIPhotoCapture;
